import { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../../Sidebar';
import ListFormPageHeader from '../../components/ListFormPageHeader';
import ListPageGradientSummary from '../../components/ListPageGradientSummary';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import { useCookies } from 'react-cookie';
import moment from 'moment';
import { displayError } from '../../helpers';
import Swal from 'sweetalert2';
import { getHTMLForTablePopup } from '../../utils/PopupUtils';
import { exportAsCSV } from '../../utils/CSVExporter';
import { getAllDeliveryChallans, printBatchReport } from '../../services/DeliveryChallanServices';
import { openPdfResponse } from '../../utils/openPdfInApp';
import { getListViewSettings } from '../../services/SettingsServices';
import {
    buildDeliveryChallanQueryParams,
    formatResolvedRange,
} from '../../utils/listViewDateRanges';
import { getDefaultListViewConfig } from '../../config/listViewSettingsConfig';
import {
    buildGradientCard,
    buildQcDeliveryChallanReportRows,
    formatAmount,
    formatQty,
    isSameListDate,
    isSameListMonth,
    sumField,
} from '../../utils/listSummaryUtils';

const VIEW_KEY = 'qc_delivery_challan';

const toCaps = (value) => String(value || '').trim().toUpperCase();

function isActiveDeliveryChallan(dc) {
    const status = dc?.status;
    if (status === true || status === 1) {
        return true;
    }
    if (status === false || status === 0) {
        return false;
    }
    const normalized = String(status ?? '').trim().toLowerCase();
    return normalized === 'true' || normalized === 'active' || normalized === '1';
}

function mapDeliveryChallanSourceRows(deliveryChallanList) {
    return (deliveryChallanList || []).map((dc) => {
        const salesOrder = dc.work_schedule?.sales_order_detail?.sales_order;
        const isActive = isActiveDeliveryChallan(dc);
        const companyBatchFlag = dc.is_batch_report_needed ?? salesOrder?.company?.is_batch_report_needed;
        const batchNeeded = companyBatchFlag === true
            || companyBatchFlag === 1
            || companyBatchFlag === 'Yes'
            || companyBatchFlag === 'yes';
        return {
            id: dc.id,
            delivery_challan_no: dc.delivery_challan_no,
            delivery_challan_date: dc.delivery_challan_date,
            delivery_challan_time: dc.delivery_challan_time,
            plant: toCaps(dc.work_schedule?.plant?.alias_name),
            customer_name: toCaps(salesOrder?.consignee?.customer?.name),
            consignee_projectname: toCaps(salesOrder?.consignee?.project_name),
            grade: toCaps(dc.work_schedule?.sales_order_detail?.product?.name),
            unit: toCaps(dc.work_schedule?.sales_order_detail?.product?.unit),
            delivery_mode: toCaps(dc.work_schedule?.delivery_mode),
            rate: dc.work_schedule?.sales_order_detail?.rate ?? 0,
            dc_qty: dc.dc_qty,
            schedule_no: dc.work_schedule?.schedule_no || '',
            customer_vehicle_no: toCaps(dc.vehicle?.name || dc.customer_vehicle_no),
            vehicle_name: toCaps(dc.vehicle?.name || dc.customer_vehicle_no),
            driver_name: dc.driver_name || '',
            total_km: dc.total_km,
            is_batch_report_needed: batchNeeded,
            is_inactive: !isActive,
        };
    });
}

function showDeliveryChallanDetailsPopup(row, cookies) {
    const details = row?.detail_rows || [];
    if (!details.length) {
        Swal.fire('No delivery challans found for this row.', '', 'info');
        return;
    }

    const totalQty = details.reduce((sum, item) => sum + (Number(item.dc_qty) || 0), 0);
    const totalAmount = details.reduce(
        (sum, item) => sum + ((Number(item.dc_qty) || 0) * (Number(row.rate) || 0)),
        0,
    );
    const totalKm = details.reduce((sum, item) => sum + (Number(item.total_km) || 0), 0);

    const popupRows = details.map((item) => ({
        ...item,
        br_print: item.id
            ? `<button type="button" class="btn btn-link qc-br-print-btn" data-dc-id="${item.id}" title="Print Batch Report" aria-label="Print Batch Report" style="padding:2px 6px;line-height:1;border:none;background:transparent;cursor:pointer;">
                <i class="fa fa-print" aria-hidden="true" style="font-size:16px;color:#1565c0;"></i>
               </button>`
            : '',
    }));

    Swal.fire({
        title: 'Delivery Challan Details',
        html: getHTMLForTablePopup(
            `${row.customer_name} — ${row.site} (${row.delivery_challan_date})`,
            [
                { key: 'delivery_challan_no', label: 'DC NO' },
                { key: 'delivery_challan_time', label: 'TIME' },
                { key: 'grade', label: 'GRADE' },
                { key: 'dc_qty_display', label: 'QTY', align: 'right' },
                { key: 'rate_display', label: 'RATE', align: 'right' },
                { key: 'amount_display', label: 'AMOUNT', align: 'right' },
                { key: 'vehicle_no', label: 'VEHICLE' },
                { key: 'driver_name', label: 'DRIVER' },
                { key: 'total_km_display', label: 'TOTAL KM', align: 'right' },
                { key: 'plant', label: 'PLANT' },
                { key: 'br_print', label: 'BATCH REPORT' },
            ],
            popupRows,
            {
                delivery_challan_no: 'TOTAL',
                delivery_challan_time: '',
                grade: '',
                dc_qty_display: formatQty(totalQty),
                rate_display: '',
                amount_display: formatAmount(totalAmount),
                vehicle_no: '',
                driver_name: '',
                total_km_display: formatQty(totalKm),
                plant: '',
                br_print: '',
            },
            'No delivery challans linked to this row.',
        ),
        width: '1200px',
        confirmButtonText: 'Close',
        didOpen: () => {
            document.querySelectorAll('.qc-br-print-btn').forEach((btn) => {
                btn.addEventListener('click', (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const deliveryId = btn.getAttribute('data-dc-id');
                    if (!deliveryId) {
                        return;
                    }
                    btn.disabled = true;
                    const iconEl = btn.querySelector('i');
                    if (iconEl) {
                        iconEl.className = 'fa fa-spinner fa-spin';
                    }
                    printBatchReport(cookies, deliveryId)
                        .then((response) => {
                            openPdfResponse(response, {
                                title: 'Batch Report',
                                downloadFileName: 'batch_report.pdf',
                            });
                        })
                        .catch((error) => {
                            displayError(
                                error.response?.data ?? error.message,
                                'BR print Failed',
                            );
                        })
                        .finally(() => {
                            btn.disabled = false;
                            if (iconEl) {
                                iconEl.className = 'fa fa-print';
                                iconEl.style.color = '#1565c0';
                            }
                        });
                });
            });
        },
    });
}

function QcDeliveryChallanReportTable() {
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);
    const [sourceRows, setSourceRows] = useState([]);
    const [listViewConfig, setListViewConfig] = useState(() => getDefaultListViewConfig(VIEW_KEY));
    const todayDateLabel = moment().format('DD-MM-YYYY');
    const token = cookies?.myToken;

    const activeRows = useMemo(
        () => sourceRows.filter((row) => !row.is_inactive),
        [sourceRows],
    );

    const reportRows = useMemo(
        () => buildQcDeliveryChallanReportRows(sourceRows),
        [sourceRows],
    );

    const todayRows = useMemo(
        () => activeRows.filter((row) => isSameListDate(row.delivery_challan_date, todayDateLabel)),
        [activeRows, todayDateLabel],
    );

    const monthRows = useMemo(
        () => activeRows.filter((row) => isSameListMonth(row.delivery_challan_date)),
        [activeRows],
    );

    const todayScheduleCount = useMemo(() => {
        const schedules = new Set(
            todayRows
                .map((row) => row.schedule_no)
                .filter((value) => value),
        );
        return schedules.size || todayRows.length;
    }, [todayRows]);

    const todayQty = useMemo(() => sumField(todayRows, 'dc_qty'), [todayRows]);
    const monthQty = useMemo(() => sumField(monthRows, 'dc_qty'), [monthRows]);
    const totalQty = useMemo(() => sumField(activeRows, 'dc_qty'), [activeRows]);

    const dateRangeLabel = useMemo(
        () => formatResolvedRange(listViewConfig, VIEW_KEY),
        [listViewConfig],
    );

    const highlightCard = useMemo(
        () => buildGradientCard('Today Schedule', todayScheduleCount, 'today'),
        [todayScheduleCount],
    );

    const summaryCards = useMemo(() => [
        buildGradientCard('Today Qty', formatQty(todayQty), 'blue'),
        buildGradientCard('This Month', formatQty(monthQty), 'teal'),
        buildGradientCard('Total Qty', formatQty(totalQty), 'purple'),
    ], [todayQty, monthQty, totalQty]);

    const columns = useMemo(() => [
        { fieldName: 'delivery_challan_date', headerName: 'DATE' },
        { fieldName: 'customer_name', headerName: 'CUSTOMER' },
        { fieldName: 'site', headerName: 'SITE' },
        { fieldName: 'unit', headerName: 'UNIT' },
        {
            fieldName: 'dc_qty_display',
            headerName: 'QTY',
            align: 'right',
            getCellStyle: () => ({
                color: '#2563eb',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: 600,
            }),
            onCellClick: (row) => showDeliveryChallanDetailsPopup(row, cookies),
        },
        { fieldName: 'delivery_mode', headerName: 'MODE OF DELIVERY' },
        { fieldName: 'rate_display', headerName: 'RATE', align: 'right' },
        { fieldName: 'amount_display', headerName: 'AMOUNT', align: 'right' },
    ], [cookies]);

    const loadReport = useCallback((config) => {
        if (!token) {
            setIsLoading(false);
            return;
        }
        const activeConfig = config || getDefaultListViewConfig(VIEW_KEY);
        setListViewConfig(activeConfig);
        setIsLoading(true);
        getAllDeliveryChallans(cookies, buildDeliveryChallanQueryParams(activeConfig))
            .then((response) => {
                const list = response?.delivery_challan_list;
                setSourceRows(mapDeliveryChallanSourceRows(Array.isArray(list) ? list : []));
            })
            .catch((error) => {
                setSourceRows([]);
                displayError(error.response?.data ?? error.message, 'Failed to load delivery challan report');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [cookies, token]);

    const handleSettingsSaved = useCallback((config) => {
        loadReport(config || getDefaultListViewConfig(VIEW_KEY));
    }, [loadReport]);

    useEffect(() => {
        if (!token) {
            setIsLoading(false);
            return;
        }
        const fallbackConfig = getDefaultListViewConfig(VIEW_KEY);
        getListViewSettings(cookies, VIEW_KEY)
            .then((response) => {
                loadReport(response?.config || fallbackConfig);
            })
            .catch(() => {
                // Old backends without qc_delivery_challan still load with a wide default range.
                loadReport(fallbackConfig);
            });
    }, [cookies, token, loadReport]);

    const handleCSVExport = () => {
        exportAsCSV(
            columns.map((col) => col.headerName),
            reportRows.map((row) => ({
                Date: row.delivery_challan_date,
                Customer: row.customer_name,
                Site: row.site,
                Unit: row.unit,
                Qty: row.dc_qty_display,
                'Mode of Delivery': row.delivery_mode,
                Rate: row.rate_display,
                Amount: row.amount_display,
            })),
        );
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="QUALITY CONTROL"
                    title="DELIVERY CHALLAN REPORT"
                    subtitle={`Aggregated delivery quantities by date, customer, site, unit, mode of delivery, and rate. Showing: ${dateRangeLabel}`}
                    viewKey={VIEW_KEY}
                    onSettingsSaved={handleSettingsSaved}
                    homeLink="/Home"
                />

                <ListPageGradientSummary
                    highlightCard={highlightCard}
                    cards={summaryCards}
                />

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="Delivery Challan Report"
                            columns={columns}
                            data={reportRows}
                            onCSVExport={handleCSVExport}
                            isLoading={isLoading}
                            hideDelete
                            hidePrint
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QcDeliveryChallanReportTable;
